import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  IconButton,
  Slider,
  Switch,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { RiPauseLine, RiPlayLine } from '@remixicon/react';
import classNames from 'classnames';
import type { Replayer, eventWithTime } from 'rrweb';

import {
  INTERACTION_LABELS,
  extractInteractions,
  formatTime,
} from '../../utils';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'var(--bui-radius-2)',
    border: '1px solid var(--bui-border-disabled)',
    overflow: 'hidden',
  },
  timelineWrapper: {
    position: 'relative',
    padding: 'var(--bui-space-1) var(--bui-space-4) 0',
  },
  timeline: {
    padding: 0,
  },
  sliderRail: {
    height: 4,
    borderRadius: 2,
    opacity: 0.2,
  },
  sliderTrack: {
    height: 4,
    borderRadius: 2,
  },
  sliderThumb: {
    width: 12,
    height: 12,
    marginTop: -4,
  },
  eventMarker: {
    position: 'absolute',
    top: 4,
    width: 5,
    height: 5,
    borderRadius: '50%',
    transform: 'translateX(-50%)',
    cursor: 'pointer',
    transition: 'transform 0.1s',
    '&:hover': {
      transform: 'translateX(-50%) scale(1.8)',
    },
  },
  clickMarker: {
    background: 'var(--bui-fg-primary)',
  },
  scrollMarker: {
    background: 'var(--bui-fg-disabled)',
  },
  inputMarker: {
    background: 'var(--bui-fg-link)',
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--bui-space-2)',
    padding: 'var(--bui-space-1) var(--bui-space-2) var(--bui-space-2)',
  },
  time: {
    fontVariantNumeric: 'tabular-nums',
    minWidth: 90,
  },
  speedGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  speedButton: {
    minWidth: 28,
    height: 24,
    padding: '0 var(--bui-space-1)',
    '&:hover': {
      background: 'var(--bui-bg-tint-hover)',
    },
  },
  speedButtonActive: {
    background: 'var(--bui-bg-tint-pressed)',
  },
  skipLabel: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap' as const,
    marginLeft: 'auto',
  },
});

const SPEED_OPTIONS = [1, 2, 4, 8];

const MARKER_CLASSES = {
  click: 'clickMarker',
  scroll: 'scrollMarker',
  input: 'inputMarker',
} as const;

type PlaybackControlsProps = {
  replayer: Replayer;
  events: eventWithTime[];
};

export const PlaybackControls = ({
  replayer,
  events,
}: PlaybackControlsProps) => {
  const classes = useStyles();
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [skipInactive, setSkipInactive] = useState(true);
  const animationFrameRef = useRef<number>();

  const meta = useMemo(() => replayer.getMetaData(), [replayer]);
  const totalTime = meta.totalTime;

  const eventMarkers = useMemo(() => {
    const interactions = extractInteractions(events);
    return interactions.map(i => ({
      ...i,
      position: ((i.timestamp - meta.startTime) / totalTime) * 100,
    }));
  }, [events, meta.startTime, totalTime]);

  // Sync play/pause state and timeline when the replayer is controlled externally
  // (e.g. seeking from the event log pauses the replayer).
  useEffect(() => {
    const onPause = () => {
      setIsPlaying(false);
      setCurrentTime(replayer.getCurrentTime());
    };

    const onStart = () => setIsPlaying(true);

    replayer.on('pause', onPause);
    replayer.on('start', onStart);

    return () => {
      replayer.off('pause', onPause);
      replayer.off('start', onStart);
    };
  }, [replayer]);

  // Polls replayer time each frame to keep the slider and timestamp in sync.
  const updateTime = useCallback(() => {
    if (replayer) {
      setCurrentTime(replayer.getCurrentTime());
    }
    animationFrameRef.current = requestAnimationFrame(updateTime);
  }, [replayer]);

  useEffect(() => {
    if (!isPlaying) return undefined;

    animationFrameRef.current = requestAnimationFrame(updateTime);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [updateTime, isPlaying]);

  const handlePlayPause = () => {
    if (isPlaying) {
      replayer.pause();
    } else {
      replayer.play(currentTime);
    }
  };

  const handleSeek = (_: any, value: number | number[]) => {
    const time = Array.isArray(value) ? value[0] : value;
    setCurrentTime(time);
    if (isPlaying) {
      replayer.play(time);
    } else {
      replayer.pause(time);
    }
  };

  const handleSpeedChange = (newSpeed: number) => {
    setSpeed(newSpeed);
    replayer.setConfig({ speed: newSpeed });
  };

  const handleSkipInactiveToggle = () => {
    const next = !skipInactive;
    setSkipInactive(next);
    const time = replayer.getCurrentTime();
    replayer.setConfig({ skipInactive: next });
    if (isPlaying) {
      // Restart playback to rebuild the timer queue with the new config.
      replayer.pause();
      replayer.play(time);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.timelineWrapper}>
        <Slider
          className={classes.timeline}
          classes={{
            rail: classes.sliderRail,
            track: classes.sliderTrack,
            thumb: classes.sliderThumb,
          }}
          value={currentTime}
          min={0}
          max={totalTime}
          onChange={handleSeek}
          aria-label="Playback timeline"
        />
        {eventMarkers.map((marker, i) => (
          <Tooltip
            key={i}
            title={`${INTERACTION_LABELS[marker.type]} at ${formatTime(marker.time, 1)}`}
            placement="top"
            arrow
          >
            <span
              className={classNames(
                classes.eventMarker,
                classes[MARKER_CLASSES[marker.type]],
              )}
              style={{ left: `${marker.position}%` }}
            />
          </Tooltip>
        ))}
      </div>

      <div className={classes.controlsRow}>
        <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
          <IconButton size="small" onClick={handlePlayPause}>
            {isPlaying ? <RiPauseLine size={18} /> : <RiPlayLine size={18} />}
          </IconButton>
        </Tooltip>

        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.time}
        >
          {formatTime(currentTime, 1)} / {formatTime(totalTime, 1)}
        </Typography>

        <Tooltip title="Skip periods of inactivity">
          <Typography
            color="textSecondary"
            component="label"
            variant="body2"
            className={classes.skipLabel}
          >
            Skip inactive
            <Switch
              size="small"
              checked={skipInactive}
              onChange={handleSkipInactiveToggle}
            />
          </Typography>
        </Tooltip>

        <div className={classes.speedGroup}>
          {SPEED_OPTIONS.map(s => (
            <Button
              key={s}
              size="small"
              variant="text"
              color="secondary"
              className={classNames(classes.speedButton, {
                [classes.speedButtonActive]: speed === s,
              })}
              onClick={() => handleSpeedChange(s)}
            >
              {s}x
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
